import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Loader2, UserCircle } from 'lucide-react';
import { usePrincipal } from '../../context/PrincipalContext';
import { EmptyState } from '@/components/ui/empty-state';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader, type PageHeaderTabs } from '@/components/ui/page-header';
import PrincipalProfilePage from '../PrincipalProfilePage';

const PANEL_ID = 'profile-panel';

/** The header of the page before an executive is chosen. */
function ProfilesHeader({ subtitle }: { subtitle?: string }) {
  return <PageHeader eyebrow="Assistant" title="Executive profiles" subtitle={subtitle} />;
}

/**
 * Assistant → Executive profiles (web app v4 plan §5.1): what you know about
 * each executive you support, at /assistant/profiles/:execId. Without an
 * executive it opens the one you're acting for, or your only one, or lists
 * them to choose from.
 */
export default function ExecutiveProfilesPage() {
  const { execId } = useParams<{ execId?: string }>();
  const { principals, activePrincipal, loading, loadFailed, reload } = usePrincipal();

  if (!execId) {
    if (loading) {
      return (
        <PageContainer>
          <ProfilesHeader />
          <div className="flex min-h-[200px] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" /></div>
        </PageContainer>
      );
    }
    // Couldn't read who they support: say so, rather than "no executives".
    if (loadFailed && principals.length === 0) {
      return (
        <PageContainer>
          <ProfilesHeader />
          <Alert variant="error">
            <AlertDescription className="flex flex-wrap items-center gap-2">
              The executives you support couldn’t be loaded.
              <Button size="sm" variant="outline" onClick={reload}>Try again</Button>
            </AlertDescription>
          </Alert>
        </PageContainer>
      );
    }
    const pick = activePrincipal ?? (principals.length === 1 ? principals[0] : null);
    if (pick) return <Navigate to={`/assistant/profiles/${encodeURIComponent(pick.id)}`} replace />;
    if (principals.length === 0) {
      return (
        <PageContainer>
          <ProfilesHeader />
          <EmptyState icon={UserCircle} title="No executives yet" body="Profiles appear here once you're supporting an executive. An executive or your admin pairs you." />
        </PageContainer>
      );
    }
    return (
      <PageContainer>
        <ProfilesHeader subtitle="Choose whose profile to open." />
        <ul className="flex max-w-[720px] flex-col gap-2">
          {principals.map(p => (
            <li key={p.id}>
              <Link to={`/assistant/profiles/${encodeURIComponent(p.id)}`}
                className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <UserCircle className="h-5 w-5 text-muted-foreground" aria-hidden />
                {p.name}
              </Link>
            </li>
          ))}
        </ul>
      </PageContainer>
    );
  }

  const tabs: PageHeaderTabs = {
    label: 'Executives',
    panelId: PANEL_ID,
    items: principals.map(p => ({
      id: p.id, label: p.name, to: `/assistant/profiles/${encodeURIComponent(p.id)}`, selected: p.id === execId,
    })),
  };

  return (
    // Each executive's profile starts afresh, so nothing open carries over.
    // Its header (with these tabs) is rebuilt too; the kit's header tabs put
    // the keyboard back on the tab just chosen.
    <PrincipalProfilePage key={execId} tabs={tabs} />
  );
}
